import { Box, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import Button from '~/components/common/Button';
import Table, { TColumn } from '~/components/common/Table/Table';
import Tooltip from '~/components/common/Tooltip';
import { getNumberWithCurrency } from '~/utils/formatter';
import themes from '~/themes';
import { useGetListHubZone } from '~/services/api/stores';
import { useLocation } from 'react-router-dom';
import { fromQueryString } from '~/utils/queryString';
import { Zone } from '~/models/stores';
import { ToolTipText } from '~/components/common/Tooltip/TooltipText';
import { StyledStatusText } from '~/pages/AdminOptions/Stores/style';
import AddIcon from '~/assets/images/icons/add-white.svg';
import { KeyboardArrowRight } from '@mui/icons-material';
import { debounce } from 'lodash';
import { DialogAddZone } from './DialogAddZone';
import { DeliveryZoneSets } from '../../types';
import { AllZoneMap } from './AllZoneMap';

type DrawnZonesProps = {
  defaultCenter?: {
    lat: number;
    lng: number;
  };
};

export const DrawnZones: React.FC<DrawnZonesProps> = ({ defaultCenter }) => {
  const [openZoneDialog, setOpenZoneDialog] = useState(false);
  const [zoneSelected, setZoneSelected] = useState<Zone>(null);
  const [zoneHover, setZoneHover] = useState<Zone>(null);
  const location = useLocation();

  const search = fromQueryString(location.search);

  const debouncedSetZoneHover = useMemo(
    () => debounce((zone) => setZoneHover(zone), 400),
    [],
  );

  useEffect(
    () => () => {
      debouncedSetZoneHover.cancel();
    },
    [],
  );

  const { data: zones, isFetching } = useGetListHubZone({
    action: 'list_all',
    parameters: {
      area_type: DeliveryZoneSets.DrawnZones,
      integration_id: search.id,
    },
  });

  const columns: TColumn[] = useMemo(
    () => [
      {
        id: 'name',
        title: 'Name',
        cellStyle: {
          textAlign: 'left',
          maxWidth: 240,
        },
        renderComponent: (item) => (
          <Box display='flex' alignItems='center'>
            <ToolTipText
              text={item?.name}
              maxLength={25}
              textNode={(v) => (
                <Typography
                  noWrap
                  data-testid='store-zone-name'
                  data-value={item.name}
                >
                  {v}
                </Typography>
              )}
            />
          </Box>
        ),
      },
      {
        id: 'status',
        title: 'Status',
        renderComponent: (item) => (
          <StyledStatusText enabled={item.enable}>
            <span className='dot' />
            {item.enable ? 'Enabled' : 'Disabled'}
          </StyledStatusText>
        ),
      },
      {
        id: 'delivery_fee',
        title: 'Delivery Fee',
        renderComponent: (item) =>
          item.delivery_fee_type === 'percentage' ? (
            `${Number(item.delivery_fee || 0).toFixed(2)}%`
          ) : (
            <Tooltip
              title={getNumberWithCurrency(item.delivery_fee || 0).tooltip}
            >
              <span>
                {getNumberWithCurrency(item.delivery_fee || 0).display}
              </span>
            </Tooltip>
          ),
      },
      {
        id: 'delivery_minimum',
        title: 'Order Minimum',
        renderComponent: (item) => (
          <Tooltip
            style={{ maxWidth: 120 }}
            title={getNumberWithCurrency(item.delivery_minimum || 0).tooltip}
          >
            <span>
              {getNumberWithCurrency(item.delivery_minimum || 0).display}
            </span>
          </Tooltip>
        ),
        cellStyle: { textAlign: 'left' },
      },
      {
        id: 'actions',
        title: '',
        cellStyle: { textAlign: 'right' },
        renderComponent: () => (
          <KeyboardArrowRight
            sx={{ color: `${themes.color.violet900} !important` }}
          />
        ),
      },
    ],
    [search],
  );

  return (
    <Stack spacing={0.5}>
      <Stack
        sx={{
          'position': 'sticky',
          'top': -14,
          'backgroundColor': 'white',
          'padding': '10px 0',
          'zIndex': 10,
          '@media (orientation: landscape) and (max-height:600px)': {
            position: 'unset',
            top: 'unset',
          },
        }}
      >
        <AllZoneMap
          allLatLngs={
            zones.filter((zone) => zone.enable).map((e) => e.long_lat) as any
          }
          activeZone={zoneHover?.long_lat as any}
          defaultCenter={defaultCenter}
          storeLocations={[defaultCenter]}
        />
      </Stack>
      {zones.length ? (
        <Table
          columns={columns}
          dataProps={zones}
          loading={isFetching}
          borderRadiusHeader={false}
          headerCellSxProps={{
            color: themes.color.gray400,
            fontSize: 12,
            paddingTop: '8px !important',
            paddingBottom: '8px !important',
            border: 'none',
          }}
          cellSxProps={{
            paddingTop: '6px !important',
            paddingBottom: '6px !important',
            border: 'none',
          }}
          onRowHover={(zone) => debouncedSetZoneHover(zone)}
          onRowClick={(item) => {
            setOpenZoneDialog(true);
            setZoneSelected(item);
          }}
        />
      ) : (
        ''
      )}
      <Stack
        direction='row'
        spacing={1}
        sx={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'white',
          padding: '10px 0',
        }}
      >
        <Button
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.4,
          }}
          buttonType='primary'
          onClick={() => setOpenZoneDialog(true)}
        >
          <img src={AddIcon} alt='add-icon' />
          Add Zone
        </Button>
      </Stack>
      {openZoneDialog && (
        <DialogAddZone
          open={openZoneDialog}
          zoneSelected={zoneSelected}
          onClose={() => {
            setOpenZoneDialog(false);
            setZoneSelected(null);
          }}
          defaultCenter={defaultCenter}
        />
      )}
    </Stack>
  );
};
